/**
 * Validated Input Components for react-hook-form
 *
 * Shared input components with built-in validation for common field types.
 * Use these in Quick Create Dialogs and forms with react-hook-form.
 *
 * All components:
 * - Work with react-hook-form's Controller
 * - Apply input filtering (prevent invalid chars)
 * - Show validation errors below the field
 * - Support required and optional variants
 */
import { useTranslation } from 'react-i18next';
import { Controller, Control, FieldValues, Path } from 'react-hook-form';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  validateEmail,
  filterEmailInput,
  validateDomain,
  filterDomainInput,
  validateUrl,
  filterUrlInput,
  validatePhone,
  filterPhoneInput,
} from '../../utils/validation';

interface BaseInputProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label: string;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * Email input with built-in validation and character filtering.
 * Validates email format on blur.
 */
export function ValidatedEmailInput<T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  disabled = false,
  autoFocus = false,
  placeholder,
  className,
}: BaseInputProps<T>) {
  const { t } = useTranslation('common');

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? t('validation.required') : undefined,
        validate: (value) => {
          const result = validateEmail(value || '');
          return result.isValid || (result.errorKey ? t(result.errorKey) : true);
        },
      }}
      render={({ field, fieldState }) => (
        <div className={cn('space-y-2', className)}>
          <Label htmlFor={name} required={required}>
            {label}
          </Label>
          <Input
            {...field}
            id={name}
            type="email"
            placeholder={placeholder}
            disabled={disabled}
            autoFocus={autoFocus}
            className={cn(fieldState.error && 'border-destructive')}
            onChange={(e) => field.onChange(filterEmailInput(e.target.value))}
          />
          {fieldState.error && (
            <p className="text-sm text-destructive">{fieldState.error.message}</p>
          )}
        </div>
      )}
    />
  );
}

/**
 * Domain input with built-in validation and character filtering.
 * Validates domain format (e.g., example.com) on blur.
 */
export function ValidatedDomainInput<T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  disabled = false,
  autoFocus = false,
  placeholder = 'example.com',
  className,
}: BaseInputProps<T>) {
  const { t } = useTranslation('common');

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? t('validation.required') : undefined,
        validate: (value) => {
          const result = validateDomain(value || '');
          return result.isValid || (result.errorKey ? t(result.errorKey) : true);
        },
      }}
      render={({ field, fieldState }) => (
        <div className={cn('space-y-2', className)}>
          <Label htmlFor={name} required={required}>
            {label}
          </Label>
          <Input
            {...field}
            id={name}
            type="text"
            placeholder={placeholder}
            disabled={disabled}
            autoFocus={autoFocus}
            className={cn(fieldState.error && 'border-destructive')}
            onChange={(e) => field.onChange(filterDomainInput(e.target.value))}
          />
          {fieldState.error && (
            <p className="text-sm text-destructive">{fieldState.error.message}</p>
          )}
        </div>
      )}
    />
  );
}

/**
 * URL input with built-in validation and character filtering.
 * Validates URL format on blur.
 */
export function ValidatedUrlInput<T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  disabled = false,
  autoFocus = false,
  placeholder = 'https://example.com',
  className,
}: BaseInputProps<T>) {
  const { t } = useTranslation('common');

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? t('validation.required') : undefined,
        validate: (value) => {
          const result = validateUrl(value || '');
          return result.isValid || (result.errorKey ? t(result.errorKey) : true);
        },
      }}
      render={({ field, fieldState }) => (
        <div className={cn('space-y-2', className)}>
          <Label htmlFor={name} required={required}>
            {label}
          </Label>
          <Input
            {...field}
            id={name}
            type="url"
            placeholder={placeholder}
            disabled={disabled}
            autoFocus={autoFocus}
            className={cn(fieldState.error && 'border-destructive')}
            onChange={(e) => field.onChange(filterUrlInput(e.target.value))}
          />
          {fieldState.error && (
            <p className="text-sm text-destructive">{fieldState.error.message}</p>
          )}
        </div>
      )}
    />
  );
}

/**
 * Phone number input with built-in validation and character filtering.
 * Allows digits, +, spaces, hyphens, parentheses, and 'x' for extensions.
 * Validates phone format on blur.
 */
export function ValidatedPhoneInput<T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  disabled = false,
  autoFocus = false,
  placeholder = '+1 (555) 123-4567',
  className,
}: BaseInputProps<T>) {
  const { t } = useTranslation('common');

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? t('validation.required') : undefined,
        validate: (value) => {
          const result = validatePhone(value || '');
          return result.isValid || (result.errorKey ? t(result.errorKey) : true);
        },
      }}
      render={({ field, fieldState }) => (
        <div className={cn('space-y-2', className)}>
          <Label htmlFor={name} required={required}>
            {label}
          </Label>
          <Input
            {...field}
            id={name}
            type="tel"
            inputMode="tel"
            // Phone numbers read left-to-right in every locale — force LTR so the
            // value and the placeholder hint stay LTR even on an RTL page.
            dir="ltr"
            placeholder={placeholder}
            disabled={disabled}
            autoFocus={autoFocus}
            className={cn('text-left', fieldState.error && 'border-destructive')}
            onChange={(e) => field.onChange(filterPhoneInput(e.target.value))}
          />
          {fieldState.error && (
            <p className="text-sm text-destructive">{fieldState.error.message}</p>
          )}
        </div>
      )}
    />
  );
}
